<template>
  <Section
    :id="id"
    :header="t('common.locationsAndDates')"
    class="proposed-shipment-location-section"
  >
    <template #header="{ headerId, header }">
      <h2 :id="headerId" class="w-3" tabindex="-1">
        {{ header }}
      </h2>

      <div
        class="route-marker w-5 flex flex-gap-1 justify-content-between align-items-center"
      >
        <div class="marker origin">
          <FontAwesomeIcon
            :icon="['fas', 'map-marker-alt']"
            aria-hidden="true"
            class="map-marker-alt p-button-icon p-button-icon-left"
          />
          <span class="city-state">
            {{ origin?.location?.cityState }}
          </span>
        </div>

        <i aria-hidden="true" class="pi pi-arrow-right text-gray-dark" />

        <div class="marker destination">
          <FontAwesomeIcon
            :icon="['fas', 'map-marker-alt']"
            aria-hidden="true"
            class="map-marker-alt p-button-icon p-button-icon-left"
          />
          <span class="city-state">
            {{ destination?.location?.cityState }}
          </span>
        </div>
      </div>
    </template>

    <div
      :id="`${id}-content`"
      class="proposed-shipment-location-section-content"
      tabindex="-1"
    >
      <ArrangeableShipmentStopList
        v-if="stops.length > 0"
        :id="`${id}-stop-list`"
        v-model:shipment="internalShipment"
        class="proposed-shipment-location-section-list flex flex-column flex-gap-1 m-0 p-0"
      />
      <p v-else>
        {{ t('common.noParam', { param: t('locations.locations', 2) }) }}
      </p>
    </div>
  </Section>
</template>

<script lang="ts" setup>
import isEqual from 'lodash/isEqual';
import { computed, Ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  ConsolidatedShipment,
  ConsolidatedShipmentStop,
} from '@/model/consolidation';

import ArrangeableShipmentStopList from '@/components/multistop/ArrangeableShipmentStopList.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  shipment: {
    type: ConsolidatedShipment,
    required: true,
  },
});
const { id, shipment } = toRefs(props);

const emit = defineEmits<{
  (event: 'update:shipment', shipment: ConsolidatedShipment): void;
}>();

const { t } = useI18n();

const internalShipment = computed({
  get() {
    return shipment.value;
  },
  set(updatedShipment: ConsolidatedShipment) {
    if (!isEqual(updatedShipment, shipment.value)) {
      emit('update:shipment', updatedShipment);
    }
  },
});

const origin = computed(() => shipment.value.origin);
const destination = computed(() => shipment.value.destination);
const stops: Ref<ConsolidatedShipmentStop[]> = computed(() => {
  return shipment.value.stops;
});
</script>

<style>
.proposed-shipment-location-section {
  .section-header {
    justify-content: space-between;

    .route-marker {
      .marker {
        display: flex;
        gap: var(--sp-1);

        .map-marker-alt {
          color: var(--stop-type-accent-color);
        }

        .city-state {
          color: var(--p-gray-400);
          font-size: 1rem;
          font-weight: 500;
        }
      }
    }
  }

  .proposed-shipment-location-section-content {
    .proposed-shipment-location-section-list {
      .location-section-label {
        color: var(--p-gray-400);
        line-height: 2em;
      }
    }
  }
}
</style>
